// /////////////////////////////////////////////////////////////
// [ファイル] ContractPanel.tsx
// [概要] 案件詳細「土地仕入」タブ（推進案件機能統合 リリース1）
//        仕入契約（地主 1 人・契約予定年月・契約日・決済日・想定/契約金額・仲介）、
//        筆（現行／閉鎖・区画への排他割当・登記候補の結び付け）、合筆・分筆の変更履歴。
//        仕入フェーズ（契約ごと・売上と同じ名前）は、編集モードでチップの横の鉛筆から浮いた入力欄で変える（5 は契約日、
//        6 は決済日（未来日でもよい）、7 は失注理由が必須。検査と履歴はバックエンド）。地積・筆数はバックエンドの計算値を表示するだけ。
//        仕入フェーズは日付と連動し、契約日を入れると 5、決済日を入れると 6 に自動で進む（バックエンド。2026-11-03 ユーザー決定）。
//        案件の失注はここの仕入フェーズの失注だけで行う（仕入契約がすべて失注になると、未完了の区画も一緒に失注になる）。
//        追加・編集・削除・フェーズ変更・筆の割当などの操作は、詳細画面の編集モードのときだけ出す（閲覧中は表示だけ）。
//        契約の内容（契約名・地主・予定年月・契約日・決済日・金額・仲介・配分基準・備考）は、編集モードで項目ごとに
//        その場で直す（ダイアログを開かない。送った項目だけ更新し、決済日と契約日の関係などはバックエンドが確かめる）。
//        新しい契約の追加は、地主・金額などを一度に入れる必要があるためダイアログで行う。
// /////////////////////////////////////////////////////////////
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel,
  IconButton, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import * as Icons from '../../constants/icons';
import { STANDARD_CTA_BUTTON_SX, STANDARD_TEXT_BUTTON_SX } from '../../constants/buttonStyles';
import { useNotification } from '../../providers/NotificationProvider';
import { useConfirm } from '../../providers/ConfirmProvider';
import LoadingView from '../LoadingView';
import { InlineAmountField, InlineEdit, InlineEditorArgs, InlineSelectField, InlineTextField, useCanEdit } from '../InlineEditField';
import { AnkenNumberPicker, InlineAnkenField } from './AnkenNumberPicker';
import CustomerPicker, { CustomerOption } from './CustomerPicker';
import { InlineCustomerField, useLostReasons } from './SfaInlineFields';
import { UnitPhaseChip } from './SfaStructureChips';
import {
  SfaContract, SfaParcel, SfaUnit, sfaStructureApi, fmtArea, fmtDate, fmtYen, fmtYm, todayIso,
  CONTRACT_PHASE, LOST_PHASE, ORDER_PHASE, PURCHASE_LOST_REASON, SELECTABLE_PHASES, purchasePhaseLabel,
  normalizeSystemNumbers, SYSTEM_NUMBER_LABEL,
} from '../../hooks/useSfaStructure';

// 列が多い表は折り返さず横スクロールさせる（見出し・セルの縦折り返しを防ぐ）
const NOWRAP_TABLE_SX = { '& th, & td': { whiteSpace: 'nowrap' } };

const dialogTitleSx = { fontSize: '0.875rem', py: 0.75, display: 'flex', alignItems: 'center', gap: 1 };
const JUDGE_LABEL: Record<string, { label: string; color: 'success' | 'info' | 'default' }> = {
  merge: { label: '合筆', color: 'success' },
  split: { label: '分筆', color: 'info' },
  none: { label: '変更なし', color: 'default' },
};

interface Props {
  projectId: string;
  contracts: SfaContract[];
  units: SfaUnit[];
  operatorId?: string;
  onChanged: () => void | Promise<void>;
}

export default function ContractPanel({ projectId, contracts, units, operatorId, onChanged }: Props) {
  const notify = useNotification();
  const confirm = useConfirm();
  const [selectedId, setSelectedId] = useState<number | null>(contracts[0]?.id ?? null);
  const [contractDialog, setContractDialog] = useState<{ open: boolean; contract: SfaContract | null }>({ open: false, contract: null });
  const [revisionOpen, setRevisionOpen] = useState(false);
  const [parcelDialog, setParcelDialog] = useState<{ open: boolean; parcel: Partial<SfaParcel> | null }>({ open: false, parcel: null });
  const [busy, setBusy] = useState(false);
  const canEdit = useCanEdit();
  const lostReasons = useLostReasons(true); // 失注理由はコードでなく名前で出す

  useEffect(() => {
    if (!contracts.some((c) => c.id === selectedId)) setSelectedId(contracts[0]?.id ?? null);
  }, [contracts, selectedId]);

  const contract = useMemo(() => contracts.find((c) => c.id === selectedId) || null, [contracts, selectedId]);
  const activeParcels = useMemo(() => (contract?.parcels || []).filter((p) => p.status === 'active'), [contract]);
  const closedParcels = useMemo(() => (contract?.parcels || []).filter((p) => p.status === 'closed'), [contract]);

  const run = async (label: string, fn: () => Promise<any>) => {
    setBusy(true);
    try {
      await fn();
      notify.success(label);
      onChanged();
      return true;
    } catch (e: any) {
      notify.error(e.message);
      return false;
    } finally {
      setBusy(false);
    }
  };

  // 契約の項目ごとのインライン編集の保存。送った項目だけを更新し、再読込を待ってから入力欄を閉じる。
  // 失敗は通知し、入力欄に理由を残す（例外を投げ直す）
  const saveContract = async (patch: Record<string, any>, label: string) => {
    if (!contract) return;
    let r: { phase?: string; phase_changed?: boolean };
    try {
      r = await sfaStructureApi.upsertContract(projectId, { id: contract.id, ...patch }, operatorId);
    } catch (e: any) {
      notify.error(e.message);
      throw e;
    }
    // 契約日・決済日の入力で仕入フェーズが進んだ（戻った）ときは、そのことも知らせる
    notify.success(`${contract.contract_name} の${label}を更新しました${r.phase_changed && r.phase ? `。仕入フェーズを「${purchasePhaseLabel(r.phase)}」にしました` : ''}`);
    await onChanged();
  };

  const assignParcel = (parcel: SfaParcel, unitId: number | null) =>
    run('筆の割当を更新しました', () => sfaStructureApi.upsertParcels(projectId, parcel.contract_id, [{ ...parcel, unit_id: unitId }]));

  const deleteContract = async (c: SfaContract) => {
    const ok = await confirm({ title: '仕入契約の削除', message: `「${c.contract_name}」を削除しますか？ 筆も一緒に削除されます（変更履歴や区画割当がある契約は削除できません）。`, confirmText: '削除する', destructive: true });
    if (!ok) return;
    run('仕入契約を削除しました', () => sfaStructureApi.deleteContract(projectId, c.id));
  };

  if (contracts.length === 0) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 1 }}>
          この案件には仕入契約がありません。追加するとタイプが「仕入あり」になり、筆・変更履歴が使えるようになります（区分の切替ではなく、契約の有無がそのまま表示に出ます）。
        </Alert>
        {canEdit && <Button startIcon={<Icons.Add />} sx={STANDARD_CTA_BUTTON_SX} onClick={() => setContractDialog({ open: true, contract: null })}>仕入契約を追加</Button>}
        <ContractDialog open={contractDialog.open} contract={null} projectId={projectId} operatorId={operatorId} onClose={() => setContractDialog({ open: false, contract: null })} onSaved={onChanged} />
      </Box>
    );
  }

  return (
    <Box>
      {busy && <LoadingView message="更新中..." height="48px" size={24} />}
      {/* 契約の切替と内容 */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
        <TextField select size="small" label="仕入契約" value={selectedId ?? ''} onChange={(e) => setSelectedId(Number(e.target.value))} sx={{ minWidth: 220 }}>
          {contracts.map((c) => <MenuItem key={c.id} value={c.id}>{c.contract_name}（{c.parcels.filter((p) => p.status === 'active').map((p) => p.chiban).join(', ') || '筆なし'}）</MenuItem>)}
        </TextField>
        {canEdit && <Button size="small" startIcon={<Icons.Add />} sx={STANDARD_TEXT_BUTTON_SX} onClick={() => setContractDialog({ open: true, contract: null })}>契約を追加</Button>}
      </Box>
      {contract && (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5, mb: 1.5, bgcolor: 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
            <InlineTextField
              value={contract.contract_name} label="契約名" required
              display={<Typography variant="subtitle2">{contract.contract_name}</Typography>}
              onSave={(v) => saveContract({ contract_name: v }, '契約名')}
            />
            <Typography variant="caption" color="text.secondary">仕入フェーズ</Typography>
            {/* 編集モードではチップの横に鉛筆が出て、その場で仕入フェーズを変える */}
            <InlineContractPhaseField contract={contract} contracts={contracts} units={units} projectId={projectId} operatorId={operatorId} onChanged={onChanged} />
            {contract.phase === LOST_PHASE && contract.lost_reason_category && (
              <Typography variant="caption" color="text.secondary">
                （{lostReasons.find((r) => r.reason_code === contract.lost_reason_category)?.reason_name || contract.lost_reason_category}{contract.lost_reason_detail ? ` ${contract.lost_reason_detail}` : ''}）
              </Typography>
            )}
            {contract.contract_overdue && <Chip size="small" label="契約予定超過" sx={{ height: 20, fontSize: '0.72rem', bgcolor: '#ffebee', color: '#c62828' }} />}
            <Box sx={{ flex: 1 }} />
            {canEdit && <Button size="small" color="error" startIcon={<Icons.Delete />} sx={STANDARD_TEXT_BUTTON_SX} onClick={() => deleteContract(contract)}>削除</Button>}
          </Box>
          {/* 契約の内容。編集モードでは項目ごとにその場で直す（ダイアログは開かない） */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 0.75, fontSize: '0.85rem' }}>
            <Field label="地主（売主）" value={
              <InlineCustomerField value={contract.seller_customer_id} name={contract.seller_name} label="地主（売主）" required emptyText="未設定"
                onSave={(id) => saveContract({ seller_customer_id: id }, '地主')} />
            } />
            {/* 案件番号 (Hozenall NET が採番し工事管理へ引き継ぐ統一コード)。ミラーから検索して選ぶ。契約ごとに複数持てる */}
            <Field label={SYSTEM_NUMBER_LABEL} span value={
              <InlineAnkenField value={contract.system_numbers} links={contract.anken_links} onSave={(v) => saveContract({ system_numbers: v }, '案件番号')} />
            } />
            <Field label="契約予定年月" value={
              <InlineTextField type="month" value={contract.expected_contract_ym} label="契約予定年月" display={<Typography component="span" variant="body2">{fmtYm(contract.expected_contract_ym)}</Typography>}
                onSave={(v) => saveContract({ expected_contract_ym: v }, '契約予定年月')} />
            } />
            <Field label="契約日" strong value={
              <InlineTextField type="date" value={contract.contract_date} label="契約日" display={<Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{fmtDate(contract.contract_date)}</Typography>}
                onSave={(v) => saveContract({ contract_date: v }, '契約日')} />
            } />
            <Field label="決済日" strong value={
              <InlineTextField type="date" value={contract.settlement_date} label="決済日"
                display={<Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{contract.settlement_date && contract.settlement_date > todayIso() ? `${fmtDate(contract.settlement_date)}（予定）` : fmtDate(contract.settlement_date)}</Typography>}
                onSave={(v) => saveContract({ settlement_date: v }, '決済日')} />
            } />
            <Field label="想定金額" value={
              <InlineAmountField value={contract.estimated_amount} label="想定金額" display={<Typography component="span" variant="body2">{fmtYen(contract.estimated_amount)}</Typography>}
                onSave={(v) => saveContract({ estimated_amount: v }, '想定金額')} />
            } />
            <Field label="契約金額（取得原価）" strong value={
              <InlineAmountField value={contract.contract_amount} label="契約金額" display={<Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>{fmtYen(contract.contract_amount)}</Typography>}
                onSave={(v) => saveContract({ contract_amount: v }, '契約金額')} />
            } />
            <Field label="仕入時仲介手数料" value={
              <InlineAmountField value={contract.brokerage_fee} label="仕入時仲介手数料" display={<Typography component="span" variant="body2">{fmtYen(contract.brokerage_fee)}</Typography>}
                onSave={(v) => saveContract({ brokerage_fee: v }, '仕入時仲介手数料')} />
            } />
            <Field label="仲介業者" value={
              <InlineCustomerField value={contract.broker_customer_id} name={contract.broker_name} label="仲介業者" corporateOnly
                onSave={(id) => saveContract({ broker_customer_id: id }, '仲介業者')} />
            } />
            <Field label="配分基準" value={
              <InlineSelectField value={contract.cost_basis} options={[{ value: 'area', label: '地積' }, { value: 'price', label: '取得価格' }]} label="配分基準"
                onSave={(v) => saveContract({ cost_basis: v }, '配分基準')} />
            } />
            <Field label="現在の筆（自動計算）" value={`${contract.active_parcel_count} 筆 ／ ${fmtArea(contract.active_area)}（当初 ${contract.original_parcel_count} 筆）`} />
            {(contract.memo || canEdit) && (
              <Field label="備考" span value={
                <InlineTextField value={contract.memo} label="備考" multiline onSave={(v) => saveContract({ memo: v }, '備考')} />
              } />
            )}
          </Box>
        </Box>
      )}

      {/* 筆 */}
      {contract && (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2">現在の筆（1 筆は 1 区画にのみ割当）</Typography>
            <Box sx={{ flex: 1 }} />
            {canEdit && (
              <>
                <Button size="small" startIcon={<Icons.Add />} sx={STANDARD_TEXT_BUTTON_SX} onClick={() => setParcelDialog({ open: true, parcel: null })}>筆を追加</Button>
                <Button size="small" startIcon={<Icons.History />} sx={STANDARD_TEXT_BUTTON_SX} onClick={() => setRevisionOpen(true)} disabled={activeParcels.length === 0}>合筆・分筆を登録</Button>
              </>
            )}
          </Box>
          <Box sx={{ overflowX: 'auto' }}><Table size="small" sx={NOWRAP_TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell>所在</TableCell><TableCell>地番</TableCell><TableCell>地目</TableCell><TableCell align="right">地積</TableCell>
                <TableCell>割当先の区画</TableCell><TableCell>登記</TableCell>{canEdit && <TableCell />}
              </TableRow>
            </TableHead>
            <TableBody>
              {activeParcels.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>{p.location || '-'}</TableCell>
                  <TableCell><strong>{p.chiban}</strong></TableCell>
                  <TableCell>{p.land_category || '-'}</TableCell>
                  <TableCell align="right">{fmtArea(p.land_area)}</TableCell>
                  <TableCell>
                    {canEdit ? (
                      <TextField select size="small" value={p.unit_id ?? ''} onChange={(e) => assignParcel(p, e.target.value === '' ? null : Number(e.target.value))} sx={{ minWidth: 150 }} disabled={busy}>
                        <MenuItem value=""><em>未割当</em></MenuItem>
                        {units.map((u) => <MenuItem key={u.id} value={u.id}>{u.unit_name}</MenuItem>)}
                      </TextField>
                    ) : (
                      units.find((u) => u.id === p.unit_id)?.unit_name || <Typography variant="caption" color="text.secondary">未割当</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.touki_property_id
                      ? <Tooltip title={`${p.touki_location || ''} ${p.touki_chiban || ''} ／ 所有者 ${p.current_owner_name || '-'}`}><Chip size="small" label="登記あり" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.72rem' }} /></Tooltip>
                      : <Typography variant="caption" color="text.secondary">未結び付け</Typography>}
                  </TableCell>
                  {canEdit && <TableCell><IconButton size="small" onClick={() => setParcelDialog({ open: true, parcel: p })}><Icons.Edit fontSize="small" /></IconButton></TableCell>}
                </TableRow>
              ))}
              {activeParcels.length === 0 && <TableRow><TableCell colSpan={canEdit ? 7 : 6} align="center" sx={{ color: 'text.secondary' }}>筆がありません。</TableCell></TableRow>}
            </TableBody>
          </Table></Box>
          {closedParcels.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              閉鎖した筆: {closedParcels.map((p) => `${p.chiban}（${fmtArea(p.land_area)}）`).join('、')}
            </Typography>
          )}
        </Box>
      )}

      {/* 変更履歴 */}
      {contract && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>変更履歴（合筆・分筆・地積更正。取消不可、訂正は打消し行）</Typography>
          <Box sx={{ overflowX: 'auto' }}><Table size="small" sx={NOWRAP_TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell>版</TableCell><TableCell>登記日</TableCell><TableCell>変更前（何から）</TableCell><TableCell>変更後（何に）</TableCell>
                <TableCell>判定</TableCell><TableCell align="right">地積 前</TableCell><TableCell align="right">地積 後</TableCell><TableCell align="right">差</TableCell><TableCell>理由</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contract.revisions.map((r) => {
                const diff = (Number(r.area_after || 0) - Number(r.area_before || 0));
                return (
                  <TableRow key={r.id}>
                    <TableCell><Chip size="small" label={`rev.${r.rev_no}`} sx={{ height: 20, fontSize: '0.72rem', bgcolor: '#ede7f6', color: '#4527a0' }} /></TableCell>
                    <TableCell>{fmtDate(r.registered_date)}</TableCell>
                    <TableCell>{r.before_chiban}（{r.before_count}筆）</TableCell>
                    <TableCell>{r.after_chiban}（{r.after_count}筆）</TableCell>
                    <TableCell><Chip size="small" label={JUDGE_LABEL[r.judge]?.label || r.judge} color={JUDGE_LABEL[r.judge]?.color || 'default'} variant="outlined" sx={{ height: 20, fontSize: '0.72rem' }} /></TableCell>
                    <TableCell align="right">{Number(r.area_before || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">{Number(r.area_after || 0).toFixed(2)}</TableCell>
                    <TableCell align="right">{diff === 0 ? '±0.00' : `${diff > 0 ? '+' : ''}${diff.toFixed(2)}`}</TableCell>
                    <TableCell>{r.area_diff_reason || ''}</TableCell>
                  </TableRow>
                );
              })}
              {contract.revisions.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ color: 'text.secondary' }}>変更履歴はまだありません。</TableCell></TableRow>}
            </TableBody>
          </Table></Box>
        </Box>
      )}

      <ContractDialog open={contractDialog.open} contract={contractDialog.contract} projectId={projectId} operatorId={operatorId}
        onClose={() => setContractDialog({ open: false, contract: null })} onSaved={onChanged} />
      {contract && (
        <>
          <ParcelDialog open={parcelDialog.open} parcel={parcelDialog.parcel} contract={contract} units={units} projectId={projectId}
            onClose={() => setParcelDialog({ open: false, parcel: null })} onSaved={onChanged} />
          <ParcelRevisionDialog open={revisionOpen} contract={contract} activeParcels={activeParcels} projectId={projectId} operatorId={operatorId}
            onClose={() => setRevisionOpen(false)} onSaved={onChanged} />
        </>
      )}
    </Box>
  );
}

// --------------------------------------------------
// 仕入フェーズ（契約ごと）。編集モードではチップの横に鉛筆が出て、浮いた入力欄で変える（ダイアログは開かない）。
// 選んだフェーズに必要な値の欄だけが増える（5 は契約日、6 は決済日（未来日でもよい）、7 は失注理由）。
// 案件の失注の入口はここだけ。仕入契約がすべて失注になると、未完了の区画も一緒に失注になり（理由は仕入失注）、
// すべて失注から戻すと、その区画も失注にする前のフェーズへ戻る。どちらも入力欄の中で前もって知らせる。
// 到達条件の検査・連動・履歴の記録はバックエンド
// --------------------------------------------------
interface ContractPhaseDraft { phase: string; contractDate: string; settlementDate: string; reason: string; detail: string }

// 保存すると区画にも連動するとき、その内容（入力欄の中に出す）
interface LostLinkage { lostUnits: SfaUnit[]; restoredUnits: SfaUnit[]; othersAllLost: boolean }

function InlineContractPhaseField({ contract, contracts, units, projectId, operatorId, onChanged }: {
  contract: SfaContract; contracts: SfaContract[]; units: SfaUnit[]; projectId: string; operatorId?: string; onChanged: () => void | Promise<void>;
}) {
  const notify = useNotification();
  const initial: ContractPhaseDraft = { phase: contract.phase, contractDate: contract.contract_date || '', settlementDate: contract.settlement_date || '', reason: '', detail: '' };
  const linkage: LostLinkage = {
    othersAllLost: contracts.every((c) => c.id === contract.id || c.phase === LOST_PHASE),
    lostUnits: units.filter((u) => u.unit_state === 'open' || u.unit_state === 'idle'),
    restoredUnits: units.filter((u) => u.phase === LOST_PHASE && u.lost_reason_category === PURCHASE_LOST_REASON),
  };
  return (
    <InlineEdit<ContractPhaseDraft>
      value={initial} label={`${contract.contract_name} の仕入フェーズ`} editMinWidth={380}
      display={<UnitPhaseChip phase={contract.phase} purchase />}
      isEqual={(a, b) => a.phase === b.phase}
      validate={(d) => {
        if (!d.phase) return 'フェーズを選んでください';
        if (d.phase === CONTRACT_PHASE && !d.contractDate) return '売買契約(5)にするときは契約日を入れてください';
        if (d.phase === ORDER_PHASE && !d.settlementDate) return '決済(6)にするときは決済日を入れてください';
        if (d.phase === LOST_PHASE && !d.reason) return '失注理由を選んでください';
        return null;
      }}
      onSave={async (d) => {
        const needContract = d.phase === CONTRACT_PHASE || d.phase === ORDER_PHASE;
        const needSettlement = d.phase === ORDER_PHASE;
        let r: { lost_units?: number; restored_units?: number };
        try {
          r = await sfaStructureApi.changeContractPhase(projectId, contract.id, {
            phase: d.phase,
            contract_date: needContract && d.contractDate !== (contract.contract_date || '') ? d.contractDate : undefined,
            settlement_date: needSettlement && d.settlementDate !== (contract.settlement_date || '') ? d.settlementDate : undefined,
            lost_reason_category: d.phase === LOST_PHASE ? d.reason : undefined,
            lost_reason_detail: d.phase === LOST_PHASE ? d.detail : undefined,
          }, operatorId);
        } catch (e: any) {
          notify.error(e.message);
          throw e;
        }
        const follow = r.lost_units !== undefined ? `仕入契約がすべて失注になったため、案件も失注にしました（区画 ${r.lost_units} 件を失注へ）`
          : r.restored_units !== undefined ? `案件の失注を取り消しました（区画 ${r.restored_units} 件を元のフェーズへ）` : '';
        notify.success(`${contract.contract_name} の仕入フェーズを「${purchasePhaseLabel(d.phase)}」に変更しました${follow ? `。${follow}` : ''}`);
        await onChanged();
      }}
      renderEditor={(args) => <ContractPhaseEditor {...args} current={contract.phase} linkage={linkage} />}
    />
  );
}

function ContractPhaseEditor({ draft, setDraft, saving, onKeyDown, current, linkage }: InlineEditorArgs<ContractPhaseDraft> & { current: string; linkage: LostLinkage }) {
  const reasons = useLostReasons();
  const set = (patch: Partial<ContractPhaseDraft>) => setDraft({ ...draft, ...patch });
  const menu = { MenuProps: { disableScrollLock: true } };
  const needContract = draft.phase === CONTRACT_PHASE || draft.phase === ORDER_PHASE;
  const needSettlement = draft.phase === ORDER_PHASE;
  // 保存すると案件の失注が変わるか（ほかの契約がすべて失注のときだけ）
  const willLose = linkage.othersAllLost && current !== LOST_PHASE && draft.phase === LOST_PHASE;
  const willRestore = linkage.othersAllLost && current === LOST_PHASE && !!draft.phase && draft.phase !== LOST_PHASE;
  const names = (us: SfaUnit[]) => (us.length ? `（${us.map((u) => u.unit_name).join('・')}）` : '');
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, pt: 0.75 }}>
      <TextField select fullWidth size="small" label="新しい仕入フェーズ" autoFocus value={draft.phase} disabled={saving}
        onChange={(e) => set({ phase: e.target.value })} slotProps={{ select: { defaultOpen: true, ...menu } }}>
        {SELECTABLE_PHASES.map((p) => <MenuItem key={p} value={p}>{purchasePhaseLabel(p)}</MenuItem>)}
      </TextField>
      {needContract && (
        <TextField fullWidth size="small" type="date" label={draft.phase === CONTRACT_PHASE ? '契約日（必須）' : '契約日'} value={draft.contractDate} disabled={saving}
          onChange={(e) => set({ contractDate: e.target.value })} onKeyDown={onKeyDown} slotProps={{ inputLabel: { shrink: true } }} />
      )}
      {needSettlement && (
        <TextField
          fullWidth size="small" type="date" label="決済日（必須）" value={draft.settlementDate} disabled={saving}
          onChange={(e) => set({ settlementDate: e.target.value })} onKeyDown={onKeyDown}
          slotProps={{ inputLabel: { shrink: true } }}
          helperText="決済予定の日付でもかまいません（売買契約の締結後）。決済日を新しく入れるときは契約日も入力してください"
        />
      )}
      {draft.phase === LOST_PHASE && (
        <>
          <TextField select fullWidth size="small" label="失注理由（必須）" value={draft.reason} disabled={saving}
            onChange={(e) => set({ reason: e.target.value })} slotProps={{ select: menu }}>
            {reasons.map((r) => <MenuItem key={r.reason_code} value={r.reason_code}>{r.reason_name}</MenuItem>)}
          </TextField>
          <TextField fullWidth size="small" label="詳細" value={draft.detail} disabled={saving} multiline minRows={2}
            onChange={(e) => set({ detail: e.target.value })} />
        </>
      )}
      {willLose && (
        <Alert severity="warning" sx={{ py: 0 }}>
          仕入契約がすべて失注になるため、案件も失注になります。
          {linkage.lostUnits.length
            ? `未完了の区画 ${linkage.lostUnits.length} 件${names(linkage.lostUnits)}も売上フェーズ「${LOST_PHASE}」（理由は仕入失注）にします。受注済みの区画は変えません。`
            : '未完了の区画はありません。'}
        </Alert>
      )}
      {willRestore && (
        <Alert severity="info" sx={{ py: 0 }}>
          案件の失注を取り消します。
          {linkage.restoredUnits.length
            ? `仕入失注で一緒に失注にした区画 ${linkage.restoredUnits.length} 件${names(linkage.restoredUnits)}も、失注にする前のフェーズへ戻します。`
            : '一緒に失注にした区画はありません。'}
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary">
        仕入フェーズは売上と同じ段階で、終点は「6.決済」です。販売区画が販売前のあいだは、一覧の抽出条件をこの仕入フェーズで判定します。変更は履歴に記録されます。
      </Typography>
    </Box>
  );
}

function Field({ label, value, strong, span }: { label: string; value: React.ReactNode; strong?: boolean; span?: boolean }) {
  return (
    <Box sx={{ gridColumn: span ? '1 / -1' : undefined }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.3 }}>{label}</Typography>
      <Typography variant="body2" component="div" sx={{ fontWeight: strong ? 600 : 400 }}>{value}</Typography>
    </Box>
  );
}

// --------------------------------------------------
// 案件番号の入力（ダイアログ・登録画面用。Enter で追加、✕で外す。区画のダイアログと登録画面でも使う）
// --------------------------------------------------

// --------------------------------------------------
// 仕入契約の登録・編集
// --------------------------------------------------
interface ContractForm {
  contract_name: string; seller: CustomerOption | null; broker: CustomerOption | null;
  expected_contract_ym: string; contract_date: string; settlement_date: string;
  estimated_amount: string; contract_amount: string; brokerage_fee: string; cost_basis: 'area' | 'price'; memo: string;
  system_numbers: string[]; // 案件番号 (Hozenall NET で採番し工事管理へ引き継ぐ。複数可)
  phase: string; // 登録時だけ選ぶ（変更は編集モードで仕入フェーズのチップから）
  parcels: { chiban: string; land_area: string; location: string }[];
}

// 登録時に選べる仕入フェーズ（失注は理由の入力が要るため、登録後に仕入フェーズのチップから）
export const NEW_CONTRACT_PHASES = SELECTABLE_PHASES.filter((p) => p !== LOST_PHASE);

export function ContractDialog({ open, contract, projectId, operatorId, onClose, onSaved }: {
  open: boolean; contract: SfaContract | null; projectId: string; operatorId?: string; onClose: () => void; onSaved: () => void;
}) {
  const notify = useNotification();
  const [f, setF] = useState<ContractForm>(empty());
  const [saving, setSaving] = useState(false);

  function empty(): ContractForm {
    return { contract_name: '', seller: null, broker: null, expected_contract_ym: '', contract_date: '', settlement_date: '', estimated_amount: '', contract_amount: '', brokerage_fee: '', cost_basis: 'area', memo: '', system_numbers: [], phase: NEW_CONTRACT_PHASES[0], parcels: [{ chiban: '', land_area: '', location: '' }] };
  }
  useEffect(() => {
    if (!open) return;
    setF(contract ? {
      contract_name: contract.contract_name || '',
      seller: contract.seller_customer_id ? { id: contract.seller_customer_id, display_name: contract.seller_name || '', customer_type: 'individual' } : null,
      broker: contract.broker_customer_id ? { id: contract.broker_customer_id, display_name: contract.broker_name || '', customer_type: 'corporate' } : null,
      expected_contract_ym: contract.expected_contract_ym || '', contract_date: contract.contract_date || '', settlement_date: contract.settlement_date || '',
      estimated_amount: contract.estimated_amount?.toString() || '', contract_amount: contract.contract_amount?.toString() || '', brokerage_fee: contract.brokerage_fee?.toString() || '',
      cost_basis: contract.cost_basis, memo: contract.memo || '', system_numbers: contract.system_numbers || [], phase: contract.phase, parcels: [],
    } : empty());
  }, [open, contract]);

  const set = (k: keyof ContractForm, v: any) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body: any = {
        id: contract?.id, contract_name: f.contract_name, seller_customer_id: f.seller?.id || null, broker_customer_id: f.broker?.id || null,
        expected_contract_ym: f.expected_contract_ym, contract_date: f.contract_date, settlement_date: f.settlement_date,
        estimated_amount: f.estimated_amount, contract_amount: f.contract_amount, brokerage_fee: f.brokerage_fee, cost_basis: f.cost_basis, memo: f.memo,
        system_numbers: normalizeSystemNumbers(f.system_numbers),
      };
      if (!contract) {
        body.phase = f.phase;
        body.parcels = f.parcels.filter((p) => p.chiban.trim()).map((p) => ({ chiban: p.chiban.trim(), land_area: p.land_area || null, location: p.location || null }));
      }
      await sfaStructureApi.upsertContract(projectId, body, operatorId);
      notify.success(contract ? '仕入契約を更新しました' : '仕入契約を追加しました（タイプは「仕入あり」になります）');
      onSaved();
      onClose();
    } catch (e: any) {
      notify.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" disableScrollLock>
      <DialogTitle sx={dialogTitleSx}><Icons.Landscape sx={{ fontSize: 18 }} />{contract ? '仕入契約の編集' : '仕入契約の追加'}</DialogTitle>
      <DialogContent dividers>
        {saving ? <LoadingView message="保存中..." height="200px" size={40} /> : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5, pt: 0.5 }}>
            <TextField size="small" label="契約名" value={f.contract_name} onChange={(e) => set('contract_name', e.target.value)} placeholder="空欄なら 契約①②… を自動付番" />
            <Box sx={{ gridColumn: 'span 2' }}><CustomerPicker label="地主（売主・1 人）" required value={f.seller} onChange={(v) => set('seller', v)} /></Box>
            <TextField size="small" type="month" label="契約予定年月" value={f.expected_contract_ym} onChange={(e) => set('expected_contract_ym', e.target.value)} slotProps={{ inputLabel: { shrink: true } }} helperText="確定後も上書きしない（予定と実績の差を残す）" />
            <TextField size="small" type="date" label="契約日" value={f.contract_date} onChange={(e) => set('contract_date', e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
            <TextField size="small" type="date" label="決済日" value={f.settlement_date} onChange={(e) => set('settlement_date', e.target.value)} slotProps={{ inputLabel: { shrink: true } }} helperText="入力時は契約日も必須" />
            <TextField size="small" label="想定金額（円）" value={f.estimated_amount} onChange={(e) => set('estimated_amount', e.target.value)} placeholder="契約前の見積額" />
            <TextField size="small" label="契約金額（円・取得原価）" value={f.contract_amount} onChange={(e) => set('contract_amount', e.target.value)} placeholder="契約後に入力" />
            <TextField size="small" label="仕入時仲介手数料（円）" value={f.brokerage_fee} onChange={(e) => set('brokerage_fee', e.target.value)} />
            <Box sx={{ gridColumn: 'span 2' }}><CustomerPicker label="仲介業者（法人）" corporateOnly value={f.broker} onChange={(v) => set('broker', v)} /></Box>
            <TextField select size="small" label="原価の配分基準" value={f.cost_basis} onChange={(e) => set('cost_basis', e.target.value)}>
              <MenuItem value="area">地積</MenuItem><MenuItem value="price">取得価格</MenuItem>
            </TextField>
            <AnkenNumberPicker value={f.system_numbers} links={contract?.anken_links} onChange={(v) => set('system_numbers', v)} sx={{ gridColumn: 'span 3' }} />
            <TextField size="small" label="備考（測量確定・解体造成の有無など）" value={f.memo} onChange={(e) => set('memo', e.target.value)} sx={{ gridColumn: 'span 3' }} multiline minRows={2} />
            {contract ? (
              <Box sx={{ gridColumn: 'span 3', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">仕入フェーズ:</Typography><UnitPhaseChip phase={contract.phase} purchase />
                <Typography variant="caption" color="text.secondary">（変更は編集モードで土地仕入タブのチップから）</Typography>
              </Box>
            ) : (
              <TextField select size="small" label="仕入フェーズ" value={f.phase} onChange={(e) => set('phase', e.target.value)} helperText="契約日を入れると 5、決済日を入れると 6 になります（決済予定の日付でも可）">
                {NEW_CONTRACT_PHASES.map((p) => <MenuItem key={p} value={p}>{purchasePhaseLabel(p)}</MenuItem>)}
              </TextField>
            )}
            {!contract && (
              <Box sx={{ gridColumn: 'span 3' }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>契約時の筆（任意。登記 DB からの候補検索は保存後の筆の編集から）</Typography>
                {f.parcels.map((p, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
                    <TextField size="small" label="所在" value={p.location} onChange={(e) => set('parcels', f.parcels.map((x, j) => j === i ? { ...x, location: e.target.value } : x))} sx={{ flex: 2 }} />
                    <TextField size="small" label="地番" value={p.chiban} onChange={(e) => set('parcels', f.parcels.map((x, j) => j === i ? { ...x, chiban: e.target.value } : x))} sx={{ flex: 1 }} />
                    <TextField size="small" label="地積（㎡）" type="number" value={p.land_area} onChange={(e) => set('parcels', f.parcels.map((x, j) => j === i ? { ...x, land_area: e.target.value } : x))} sx={{ flex: 1 }} />
                    <IconButton size="small" onClick={() => set('parcels', f.parcels.filter((_, j) => j !== i))}><Icons.Close fontSize="small" /></IconButton>
                  </Box>
                ))}
                <Button size="small" startIcon={<Icons.Add />} sx={STANDARD_TEXT_BUTTON_SX} onClick={() => set('parcels', [...f.parcels, { chiban: '', land_area: '', location: '' }])}>筆を追加</Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX} disabled={saving}>キャンセル</Button>
        <Button onClick={save} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX} disabled={saving}>保存する</Button>
      </DialogActions>
    </Dialog>
  );
}

// --------------------------------------------------
// 筆の追加・編集（登記候補の結び付けを含む）
// --------------------------------------------------
function ParcelDialog({ open, parcel, contract, units, projectId, onClose, onSaved }: {
  open: boolean; parcel: Partial<SfaParcel> | null; contract: SfaContract; units: SfaUnit[]; projectId: string; onClose: () => void; onSaved: () => void;
}) {
  const notify = useNotification();
  const [f, setF] = useState<Partial<SfaParcel>>({});
  const [query, setQuery] = useState('');
  const [cands, setCands] = useState<any[]>([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setF(parcel ? { ...parcel } : { location: '', chiban: '', land_category: '', land_area: null, unit_id: null, touki_property_id: null });
    setQuery(parcel?.location ? `${parcel.location} ${parcel.chiban || ''}` : '');
    setCands([]);
  }, [open, parcel]);

  const search = async () => {
    setSearching(true);
    try { setCands((await sfaStructureApi.searchTouki(query)).rows); } catch (e: any) { notify.error(e.message); } finally { setSearching(false); }
  };
  const pick = (t: any) => setF((p) => ({ ...p, touki_property_id: t.id, location: t.location, chiban: t.chiban, land_category: t.land_category || p.land_category, land_area: t.land_area ?? p.land_area }));

  const save = async () => {
    setSaving(true);
    try {
      await sfaStructureApi.upsertParcels(projectId, contract.id, [{ ...f, unit_id: f.unit_id ?? null }]);
      notify.success(parcel ? '筆を更新しました' : '筆を追加しました');
      onSaved(); onClose();
    } catch (e: any) { notify.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" disableScrollLock>
      <DialogTitle sx={dialogTitleSx}><Icons.Landscape sx={{ fontSize: 18 }} />{parcel ? `筆の編集: ${parcel.chiban}` : '筆の追加'}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, pt: 0.5 }}>
          <TextField size="small" label="所在" value={f.location || ''} onChange={(e) => setF({ ...f, location: e.target.value })} sx={{ gridColumn: 'span 2' }} />
          <TextField size="small" label="地番" required value={f.chiban || ''} onChange={(e) => setF({ ...f, chiban: e.target.value })} />
          <TextField size="small" label="地目" value={f.land_category || ''} onChange={(e) => setF({ ...f, land_category: e.target.value })} />
          <TextField size="small" label="地積（㎡）" type="number" value={f.land_area ?? ''} onChange={(e) => setF({ ...f, land_area: e.target.value === '' ? null : Number(e.target.value) })} />
          <TextField select size="small" label="割当先の区画" value={f.unit_id ?? ''} onChange={(e) => setF({ ...f, unit_id: e.target.value === '' ? null : Number(e.target.value) })}>
            <MenuItem value=""><em>未割当</em></MenuItem>
            {units.map((u) => <MenuItem key={u.id} value={u.id}>{u.unit_name}</MenuItem>)}
          </TextField>
          <Box sx={{ gridColumn: 'span 2', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">登記物件:</Typography>
            {f.touki_property_id ? <Chip size="small" label={`結び付け済み (ID ${f.touki_property_id})`} color="success" variant="outlined" onDelete={() => setF({ ...f, touki_property_id: null })} /> : <Typography variant="caption">未結び付け（任意）</Typography>}
          </Box>
        </Box>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>登記 DB から候補を探す（所在・地番で検索。選ぶと所在・地番・地積が入ります）</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
            <TextField size="small" fullWidth value={query} onChange={(e) => setQuery(e.target.value)} placeholder="例: 高須台 12" onKeyDown={(e) => { if (e.key === 'Enter') search(); }} />
            <Button startIcon={<Icons.Search />} sx={{ ...STANDARD_CTA_BUTTON_SX, flexShrink: 0, whiteSpace: 'nowrap' }} onClick={search} disabled={searching || query.trim().length < 2}>検索</Button>
          </Box>
          {searching ? <LoadingView message="検索中..." height="80px" size={28} /> : cands.length > 0 && (
            <Table size="small">
              <TableHead><TableRow><TableCell>所在</TableCell><TableCell>地番</TableCell><TableCell>地目</TableCell><TableCell align="right">地積</TableCell><TableCell>所有者</TableCell><TableCell /></TableRow></TableHead>
              <TableBody>
                {cands.map((t) => (
                  <TableRow key={t.id} hover selected={t.id === f.touki_property_id}>
                    <TableCell>{t.location}</TableCell><TableCell>{t.chiban}</TableCell><TableCell>{t.land_category || '-'}</TableCell>
                    <TableCell align="right">{fmtArea(t.land_area)}</TableCell><TableCell>{t.current_owner_name || '-'}{t.has_lien ? '（抵当権あり）' : ''}</TableCell>
                    <TableCell><Button size="small" sx={STANDARD_TEXT_BUTTON_SX} onClick={() => pick(t)}>この物件にする</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX} disabled={saving}>キャンセル</Button>
        <Button onClick={save} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX} disabled={saving || !(f.chiban || '').trim()}>保存する</Button>
      </DialogActions>
    </Dialog>
  );
}

// --------------------------------------------------
// 合筆・分筆・地積更正の登録
// --------------------------------------------------
function ParcelRevisionDialog({ open, contract, activeParcels, projectId, operatorId, onClose, onSaved }: {
  open: boolean; contract: SfaContract; activeParcels: SfaParcel[]; projectId: string; operatorId?: string; onClose: () => void; onSaved: () => void;
}) {
  const notify = useNotification();
  const [date, setDate] = useState('');
  const [before, setBefore] = useState<number[]>([]);
  const [after, setAfter] = useState<{ chiban: string; land_area: string }[]>([{ chiban: '', land_area: '' }]);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDate(new Date().toISOString().slice(0, 10)); setBefore(activeParcels.length === 1 ? [activeParcels[0].id] : []); setAfter([{ chiban: '', land_area: '' }]); setReason('');
  }, [open, activeParcels]);

  const beforeRows = activeParcels.filter((p) => before.includes(p.id));
  const validAfter = after.filter((a) => a.chiban.trim());
  const areaBefore = beforeRows.reduce((s, p) => s + Number(p.land_area || 0), 0);
  const areaAfter = validAfter.reduce((s, a) => s + Number(a.land_area || 0), 0);
  const judge = beforeRows.length && validAfter.length ? (validAfter.length < beforeRows.length ? 'merge' : validAfter.length > beforeRows.length ? 'split' : 'none') : null;
  const needsCheck = beforeRows.length >= 2 || validAfter.length >= 2;
  const mismatch = needsCheck && Math.abs(areaBefore - areaAfter) > 0.005;

  const save = async () => {
    setSaving(true);
    try {
      const r = await sfaStructureApi.addRevision(projectId, contract.id, { registered_date: date, before_parcel_ids: before, after: validAfter.map((a) => ({ chiban: a.chiban.trim(), land_area: a.land_area || 0 })), area_diff_reason: reason }, operatorId);
      notify.success(`変更履歴 rev.${r.rev_no} を登録しました（判定: ${JUDGE_LABEL[r.judge]?.label || r.judge}）`);
      onSaved(); onClose();
    } catch (e: any) { notify.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" disableScrollLock>
      <DialogTitle sx={dialogTitleSx}><Icons.History sx={{ fontSize: 18 }} />変更履歴の追加（移転登記）: {contract.contract_name}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1.5, flexWrap: 'wrap' }}>
          <TextField size="small" type="date" label="登記日" value={date} onChange={(e) => setDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">判定（自動）:</Typography>
            {judge ? <Chip size="small" label={JUDGE_LABEL[judge].label} color={JUDGE_LABEL[judge].color} variant="outlined" /> : <Typography variant="caption">-</Typography>}
          </Box>
        </Box>
        <Typography variant="subtitle2">変更前（何から）</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          {activeParcels.map((p) => (
            <FormControlLabel key={p.id} control={<Checkbox size="small" checked={before.includes(p.id)} onChange={(e) => setBefore(e.target.checked ? [...before, p.id] : before.filter((x) => x !== p.id))} />}
              label={`${p.chiban}（${fmtArea(p.land_area)}${p.unit_name ? `・${p.unit_name}` : ''}）`} />
          ))}
        </Box>
        <Typography variant="subtitle2">変更後（何に）</Typography>
        {after.map((a, i) => (
          <Box key={i} sx={{ display: 'flex', gap: 1, mb: 0.75 }}>
            <TextField size="small" label="地番" value={a.chiban} onChange={(e) => setAfter(after.map((x, j) => j === i ? { ...x, chiban: e.target.value } : x))} sx={{ flex: 1 }} />
            <TextField size="small" label="地積（㎡）" type="number" value={a.land_area} onChange={(e) => setAfter(after.map((x, j) => j === i ? { ...x, land_area: e.target.value } : x))} sx={{ flex: 1 }} />
            <IconButton size="small" onClick={() => setAfter(after.filter((_, j) => j !== i))} disabled={after.length === 1}><Icons.Close fontSize="small" /></IconButton>
          </Box>
        ))}
        <Button size="small" startIcon={<Icons.Add />} sx={STANDARD_TEXT_BUTTON_SX} onClick={() => setAfter([...after, { chiban: '', land_area: '' }])}>変更後の筆を追加</Button>
        <Box sx={{ mt: 1.5, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2">地積合計 前 <strong>{areaBefore.toFixed(2)}</strong> ㎡ → 後 <strong>{areaAfter.toFixed(2)}</strong> ㎡</Typography>
          {mismatch && <Alert severity="warning" sx={{ py: 0 }}>地積合計が一致しません。地積更正などの理由を入れると確定できます</Alert>}
        </Box>
        <TextField size="small" fullWidth label="地積が一致しない理由（地積更正など）" value={reason} onChange={(e) => setReason(e.target.value)} sx={{ mt: 1 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          変更前の筆は閉鎖、変更後の筆は新規になります。変更前の筆が 1 つの区画に割り当てられていれば、変更後の筆も同じ区画へ引き継ぎます。
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={STANDARD_TEXT_BUTTON_SX} disabled={saving}>キャンセル</Button>
        <Button onClick={save} startIcon={<Icons.Save />} sx={STANDARD_CTA_BUTTON_SX} disabled={saving || !before.length || !validAfter.length || (mismatch && !reason.trim())}>登録する</Button>
      </DialogActions>
    </Dialog>
  );
}
